/**
 * Checkout action builders — the boundary between cart UI and the money-path
 * HTTP routes. Three thin async functions, each owning one `fetch` to one
 * route, each running its `@kraahl/contract` Zod schema before the network
 * call. This is the "Functional Core / Imperative Shell" seam for the cart
 * checkout flow:
 *
 *   components/cart/paypal-checkout.tsx ── intent ──▶ builders here
 *                                                       │
 *                                                       ├── safeParse (boundary)
 *                                                       └── fetch + parse JSON
 *
 * Why this exists:
 *  - Inline `fetch('/api/paypal-orders/...')` calls inside a 700-line React
 *    component made the request shape implicit, untestable without spinning
 *    up the component tree, and prone to drift from the canonical contract.
 *  - The same money-path is now consumed by mobile (`apps/mobile/lib/api-paypal.ts`).
 *    Web + mobile must share one declaration of what the wire looks like;
 *    importing the contract here is the enforcement point on the web side.
 *  - Component tests can now mock `@/lib/payments/checkout-actions` and
 *    assert "click → builder called with X" without faking `fetch`.
 *
 * Design notes:
 *  - Builders accept INTENT-style args (what the caller naturally has from
 *    cart state + shipping form), not the raw wire body. Assembly is the
 *    builder's job — that's the whole point of the seam.
 *  - Schema parse failures THROW. Rationale: the contract is the boundary;
 *    a failure here is a programmer bug (mismatch between caller and the
 *    shape the server expects). The server would 422 anyway — failing
 *    client-side at the responsible line surfaces the bug sooner. Mobile
 *    chose warn-and-continue (server-authoritative) for a different
 *    trade-off; we revisit if a real UX regression shows up.
 *  - Response shape is NOT yet pinned by Zod. Per the strategy doc, that is
 *    a separate iteration — locking response shape now risks churn while
 *    server-side response normalisation is still in flight.
 *  - Each builder is single-responsibility (one route per builder, no `mode`
 *    parameter). See FRONTEND_WIRING_STRATEGY.md §2 principle 5.
 *
 * @see .tondocs/FRONTEND_WIRING_STRATEGY.md §4 #1 — this is item #1
 * @see .tondocs/skills/hex.md §2 — Functional Core / Imperative Shell
 * @see packages/contract/src/checkout-intent.ts — CreateOrderRequest contract
 * @see packages/contract/src/capture-order.ts — CaptureOrderRequest contract
 * @see packages/contract/src/stripe-checkout-session.ts — Stripe contract
 * @see apps/mobile/lib/api-paypal.ts — mobile sibling using the same schemas
 */

import {
  CaptureOrderRequestSchema,
  CaptureOrderResponseSchema,
  CreateOrderRequestSchema,
  CreateOrderResponseSchema,
  CreateStripeCheckoutSessionRequestSchema,
  CreateStripeCheckoutSessionResponseSchema,
  narrowCaptureResponse,
  parseApiResponse,
  type CaptureOrderRequest,
  type CheckoutFundingSource,
  type CheckoutIntent,
  type CheckoutPayload,
  type CheckoutPaymentSource,
  type CreateOrderRequest,
  type CreateStripeCheckoutSessionRequest,
  type CreateStripeCheckoutSessionResponse,
} from "@kraahl/contract";
import { z } from "zod";
import {
  PAYMENTS_ERROR_CODES,
  PaymentCreateError,
  type PaymentsErrorCode,
} from "@/lib/payments/error-codes";

// ---------------------------------------------------------------------------
// Shared error
// ---------------------------------------------------------------------------

/**
 * Thrown when a request body fails the contract Zod parse before fetch. This
 * is a programmer-bug signal: the caller assembled an intent the contract
 * does not accept. The error message includes the route path so the failure
 * is greppable; details carry Zod's issue list for the responsible developer
 * (NOT shown to the buyer).
 */
export class CheckoutContractError extends Error {
  readonly route: string;
  readonly issues: ReadonlyArray<{ path: string; message: string }>;
  constructor(
    route: string,
    issues: ReadonlyArray<{ path: string; message: string }>,
  ) {
    super(
      `[checkout-actions] contract parse failed for ${route}: ${issues
        .map((i) => `${i.path}: ${i.message}`)
        .join("; ")}`,
    );
    this.name = "CheckoutContractError";
    this.route = route;
    this.issues = issues;
  }
}

// ---------------------------------------------------------------------------
// G1 — createPayPalOrder
// ---------------------------------------------------------------------------

export interface CreatePayPalOrderArgs {
  /** Required. The server resolves intent → purchase_units server-side. */
  intent: CheckoutIntent;
  /**
   * Trusted client slice for native digital-wallet flows (Google Pay today,
   * Apple Pay reserved). Forwarded as-is to the server.
   */
  paymentSource?: CheckoutPaymentSource;
  /**
   * Rail hint. Drives PayPal `soft_descriptor` server-side: `"card"` →
   * `"KRAAHL <seller>"`, anything else → bare `"KRAAHL"`. See FX #1 in the
   * 2026-05-29 UNAUTHORISED-chargeback incident (`.tondocs/paypal/`).
   */
  fundingSource?: CheckoutFundingSource;
  /**
   * Sandbox-only PayPal-Mock-Response trigger. The server validates it
   * against an allowlist + sandbox + demo flag before honouring.
   */
  mockCode?: string;
}

/**
 * Server response from `POST /api/paypal-orders/create`. Shape is the
 * subset the cart UI consumes; the route returns more (debugId, details)
 * that callers can read from the returned `unknown`-typed extras if needed
 * — promoted to a typed field only when a caller actually depends on it.
 */
export interface CreatePayPalOrderResult {
  /** PayPal Order ID. Required on success. */
  id: string;
  /** Optional PayPal `approvalUrl` for redirect flows (native deep link). */
  approvalUrl?: string;
  /** Optional PayPal debug id, surfaced in error messages by the component. */
  debugId?: string;
}

/**
 * Build the create-order request, schema-check it, POST it, and return the
 * minimal success shape. Errors:
 *   - `CheckoutContractError` — body failed `CreateOrderRequestSchema`.
 *   - {@link PaymentCreateError} — server rejected (non-2xx). Carries the
 *     typed `code` from {@link PAYMENTS_ERROR_CODES}, the original PayPal
 *     `paypalIssue` (when surfaced by the route), and `debugId`. The
 *     component switches on `error.code` — no regex on `err.message`.
 *
 *     `code` mapping:
 *     - 409 + `SELLER_PAYPAL_RESTRICTED` → seller account can't receive
 *       (PAYEE_ACCOUNT_RESTRICTED et al, T1).
 *     - 422 + `PAYPAL_PLATFORM_FEE_CAP_EXCEEDED` → §O.2.2 fee-cap.
 *     - Anything else → `PAYPAL_CREATE_FAILED` (legacy generic).
 *
 *     `message` mirrors the legacy `"Create: <issue> (debug: <id>)"` format
 *     so console.error log lines + Axiom queries pinned on that string keep
 *     working.
 */
/**
 * POST helper for the three money-path calls below. Two hardenings
 * backported from the mobile fetch layer (parity audit 2026-07-04, P2):
 *
 *  - SINGLE retry on 401 after a short delay — the parallel-mount Supabase
 *    refresh-token race (.tondocs/infra/AUTH_REFRESH_RACE.md) makes a lone
 *    401 transient on writes; one delayed retry rides the refreshed cookie.
 *    Only 401 retries: 4xx are real rejections, and a 5xx on capture can be
 *    "captured-with-non-2xx" (money moved) which the caller must inspect.
 *
 *  - optional Idempotency-Key header (create route relays it to PayPal as
 *    PayPal-Request-Id) so the auto-retry — or a rapid double submit — maps
 *    to ONE PayPal order instead of minting an orphan duplicate. The SAME
 *    key is deliberately reused across the retry pair.
 */
async function moneyPost(
  url: string,
  body: unknown,
  opts?: { idempotencyKey?: string },
): Promise<Response> {
  const doFetch = () =>
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(opts?.idempotencyKey
          ? { "Idempotency-Key": opts.idempotencyKey }
          : {}),
      },
      body: JSON.stringify(body),
    });
  const first = await doFetch();
  if (first.status !== 401) return first;
  await new Promise((resolve) => setTimeout(resolve, 250));
  return doFetch();
}

export async function createPayPalOrder(
  args: CreatePayPalOrderArgs,
): Promise<CreatePayPalOrderResult> {
  const body: CreateOrderRequest = {
    intent: args.intent,
    ...(args.paymentSource ? { paymentSource: args.paymentSource } : {}),
    ...(args.fundingSource ? { fundingSource: args.fundingSource } : {}),
    ...(args.mockCode ? { mockCode: args.mockCode } : {}),
  };

  const parsed = CreateOrderRequestSchema.safeParse(body);
  if (!parsed.success) {
    throw new CheckoutContractError(
      "/api/paypal-orders/create",
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    );
  }

  // Per-attempt idempotency key (mirror of the mobile client): a deliberate
  // re-click is a NEW attempt with a fresh key; the internal 401 retry inside
  // moneyPost reuses this one. Relayed as PayPal-Request-Id server-side.
  const response = await moneyPost("/api/paypal-orders/create", body, {
    idempotencyKey: crypto.randomUUID(),
  });
  if (!response.ok) {
    // Error envelope — opaque shape (`ApiErrorEnvelope` + optional debugId
    // + optional paypalIssue). Read it directly rather than running it
    // through the success-shape parser.
    const errorBody = (await response.json().catch(() => ({}))) as {
      error?: string;
      code?: string;
      debugId?: string;
      paypalIssue?: string;
      details?: { details?: { issue?: string }[]; name?: string };
    };
    console.error("Failed to create order:", errorBody);

    // Typed code is preferred — the route returns one of PAYMENTS_ERROR_CODES.
    // Fall back to PAYPAL_CREATE_FAILED for legacy / unknown envelopes (e.g.
    // a proxy 5xx that doesn't carry the contract shape).
    const code: PaymentsErrorCode =
      typeof errorBody?.code === "string" &&
      // Defence-in-depth: only accept codes the registry knows. Unknown
      // strings collapse to the generic bucket so future server-side codes
      // don't accidentally short-circuit the component switch.
      (Object.values(PAYMENTS_ERROR_CODES) as readonly string[]).includes(
        errorBody.code,
      )
        ? (errorBody.code as PaymentsErrorCode)
        : PAYMENTS_ERROR_CODES.PAYPAL_CREATE_FAILED;

    // PayPal issue: prefer the structured top-level field the route now
    // populates for seller-restriction (T1). Fall back to the legacy nested
    // `details.details[0].issue` shape for older error envelopes.
    const paypalIssue =
      (typeof errorBody?.paypalIssue === "string" && errorBody.paypalIssue) ||
      errorBody?.details?.details?.[0]?.issue ||
      undefined;

    const humanDetail =
      paypalIssue ||
      errorBody?.details?.name ||
      errorBody?.error ||
      "Failed to create order";
    const debugId =
      typeof errorBody?.debugId === "string" ? errorBody.debugId : undefined;

    throw new PaymentCreateError({
      code,
      paypalIssue,
      debugId,
      message: `Create: ${humanDetail} (debug: ${debugId || "n/a"})`,
    });
  }
  // Boundary parse — throws ApiResponseParseError on shape drift.
  const result = await parseApiResponse(response, CreateOrderResponseSchema);
  return {
    id: result.id,
    approvalUrl: result.approvalUrl,
    debugId: result.debugId,
  };
}

// ---------------------------------------------------------------------------
// G2 — capturePayPalOrder
// ---------------------------------------------------------------------------

export interface CapturePayPalOrderArgs {
  /** PayPal Order ID from `createPayPalOrder`. */
  orderId: string;
  /**
   * Optional checkout metadata. The server uses it for (a) server-side
   * order persistence and (b) reconciliation recovery if persistence fails
   * after capture. Opaque at this layer — concrete shape is
   * `lib/checkout-payload.ts::CheckoutPayload`.
   */
  checkoutData?: Record<string, unknown>;
  /** Sandbox-only negative-testing code. */
  mockCode?: string;
}

/**
 * Capture response. Shape mirrors what the component reads back:
 *  - `capture` — the raw PayPal capture payload (purchase_units, etc.).
 *    The component digs `purchase_units[].payments.captures[].id` out of it.
 *  - `orderResult` — server-side order persistence outcome. May be `undefined`
 *    or `success: false` even on a 2xx response when capture succeeded but
 *    order persistence is queued for reconciliation recovery (see
 *    `.tondocs/orders/RECONCILIATION_JOB.md §0`).
 *  - `code` / `paymentStatus` — top-level codes the server may attach
 *    (`DUPLICATE_ORDER`, `ORDER_CREATION_FAILED`, `"captured_order_pending"`).
 *
 * The whole object is `Record<string, unknown>` at the type seam because no
 * caller currently depends on the deep PayPal shape statically — the
 * component reads it dynamically. Tightening this is a separate response-side
 * pass.
 */
export interface CapturePayPalOrderResult {
  capture?: Record<string, unknown>;
  orderResult?: {
    success?: boolean;
    orderId?: string;
    orderNumber?: string;
    auctionSettled?: boolean;
    offerSettled?: boolean;
    error?: string;
  };
  code?: string;
  paymentStatus?: string;
  // Pass-through for fields the caller may read defensively (debugId,
  // summary, etc.) without committing to a typed shape yet.
  [k: string]: unknown;
}

/**
 * Build the capture request, schema-check it, POST it, and return the
 * partial-failure-aware result. Mirrors the existing component logic:
 *
 *  - 2xx → return the full result.
 *  - non-2xx WITH `result.capture` → still return it (capture succeeded,
 *    order persistence failed; caller decides not to throw so reconciliation
 *    can recover).
 *  - non-2xx WITHOUT `result.capture` → throw with the `"Capture: ..."`
 *    prefix the component's `classifyPaymentError` expects.
 */
export async function capturePayPalOrder(
  args: CapturePayPalOrderArgs,
): Promise<CapturePayPalOrderResult> {
  const body: CaptureOrderRequest = {
    order_id: args.orderId,
    ...(args.checkoutData !== undefined ? { checkoutData: args.checkoutData } : {}),
    ...(args.mockCode ? { mockCode: args.mockCode } : {}),
  };

  const parsed = CaptureOrderRequestSchema.safeParse(body);
  if (!parsed.success) {
    throw new CheckoutContractError(
      "/api/paypal-orders/capture",
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    );
  }

  // 401-retry only — the capture route is idempotent on paypal_order_id, and
  // a 401 means the handler never ran (no money implications).
  const response = await moneyPost("/api/paypal-orders/capture", body);

  // We must inspect `capture` BEFORE deciding throw-vs-return to preserve the
  // "money moved, order persistence failed → propagate so reconciliation can
  // recover" rule (see app/api/paypal-orders/capture/route.ts §"capture
  // already succeeded" branch). `Response.json()` is single-shot, so we clone
  // the response and peek the raw body once; on the valid-body path we then
  // run `parseApiResponse` against the clone for the boundary-typed parse.
  const rawForPeek = (await response.clone().json().catch(() => ({}))) as {
    capture?: unknown;
    error?: string;
    debugId?: string;
    details?: { details?: { issue?: string }[]; name?: string };
    orderResult?: { error?: string };
  };

  // Distinguish hard capture failure (no money moved) from partial-success
  // (money moved, order persistence failed). The latter must propagate so
  // the component can render the "paid, processing" state and reconciliation
  // can recover; throwing here would make the caller treat money-moved as
  // money-failed.
  if (!response.ok && !rawForPeek?.capture) {
    console.error("Failed to capture order:", rawForPeek);
    const details =
      rawForPeek?.details?.details?.[0]?.issue ||
      rawForPeek?.details?.name ||
      rawForPeek?.error ||
      "Failed to capture order";
    const debugId =
      typeof rawForPeek?.debugId === "string" ? rawForPeek.debugId : "n/a";
    throw new Error(
      `Capture: ${details} (debug: ${debugId}, order: ${args.orderId})`,
    );
  }
  if (!response.ok && rawForPeek?.capture) {
    console.error(
      "[CAPTURE] Payment captured but order creation failed. Reconciliation will recover.",
      rawForPeek.orderResult,
    );
  }

  // Boundary parse — the body now matches CaptureOrderResponseSchema (capture
  // present + summary, by the route's wire contract on every branch that
  // reaches here, including 4xx-with-capture). Narrow into the discriminated
  // union and map to the flat result shape current callers consume.
  const parsedResponse = await parseApiResponse(
    response,
    CaptureOrderResponseSchema,
  );
  const narrowed = narrowCaptureResponse(parsedResponse);

  switch (narrowed.kind) {
    case "fresh":
      return {
        capture: narrowed.capture as Record<string, unknown> | undefined,
        orderResult: narrowed.orderResult,
        debugId: narrowed.debugId,
        summary: parsedResponse.summary,
      };
    case "duplicate":
      return {
        capture: narrowed.capture as Record<string, unknown> | undefined,
        orderResult: narrowed.orderResult,
        code: narrowed.code,
        summary: parsedResponse.summary,
      };
    case "pending":
      return {
        capture: narrowed.capture as Record<string, unknown> | undefined,
        orderResult: narrowed.orderResult,
        debugId: narrowed.debugId,
        code: narrowed.code,
        paymentStatus: narrowed.paymentStatus,
        summary: parsedResponse.summary,
      };
    default: {
      const _: never = narrowed;
      throw new Error(
        `capturePayPalOrder: unreachable capture-response kind: ${JSON.stringify(_)}`,
      );
    }
  }
}

// ---------------------------------------------------------------------------
// G3 — createStripeCheckoutSession
// ---------------------------------------------------------------------------

export interface CreateStripeCheckoutSessionArgs {
  /**
   * Canonical {@link CheckoutPayload} from cart state. The contract schema
   * (`CheckoutPayloadSchema`) accepts this shape verbatim — both PayPal and
   * Stripe rails consume the same discriminated `flat_rate | live_rate`
   * union under PR-L (Sibling-F · F13). Server re-derives totals + folds
   * `externalShipping` into `application_fee_amount` on the live_rate
   * branch and 409s on mismatch.
   */
  checkoutData: CheckoutPayload;
}

/**
 * Stripe response — tagged result over the checkout-session ADT
 * ({@link CreateStripeCheckoutSessionResponse}). On success, the result IS
 * the ADT (spread, plus `ok: true`) — consumers branch on `.kind`
 * (`"mount"` → mount `<PaymentElement />` + `<ExpressCheckoutElement />` under `<CheckoutElementsProvider stripe options={{ clientSecret, elementsOptions: { appearance } }}>` with `loadStripe({ stripeAccount: sellerStripeAccountId })`,
 * `"already_paid"` → route straight to `/checkout/stripe-return`), ending
 * in `default: unreachable(result: never)` (see `handleStripeCheckout`,
 * Step 6 of the plan). On failure, `error`/`message` mirror the legacy
 * envelope so the existing banner-rendering code keeps working.
 *
 * @see packages/contract/src/stripe-checkout-session.ts — the ADT this wraps
 * @see .tondocs/stripe/CHECKOUT_FORM_PROVIDER_MIGRATION.md — Embedded Form migration
 * @see .tondocs/stripe/EMBEDDED_CHECKOUT_PLAN.md §4 — Builder step (predecessor plan)
 */
export type CreateStripeCheckoutSessionResult =
  | ({ ok: true } & CreateStripeCheckoutSessionResponse)
  | { ok: false; message: string; error?: string };

/**
 * Build the Stripe checkout-session request, schema-check it, POST it, and
 * return a structured result. Does NOT throw on non-2xx OR on a response
 * that fails the boundary parse — the component's existing
 * handleStripeCheckout reads `data.message`/`data.error` off the returned
 * result and shows the banner rather than relying on exception
 * classification. Throws only on:
 *  - `CheckoutContractError` (programmer bug — request contract mismatch)
 *  - network failure (caller catches and surfaces "Stripe checkout failed.")
 */
export async function createStripeCheckoutSession(
  args: CreateStripeCheckoutSessionArgs,
): Promise<CreateStripeCheckoutSessionResult> {
  const body: CreateStripeCheckoutSessionRequest = {
    checkoutData: args.checkoutData,
  };

  const parsed = CreateStripeCheckoutSessionRequestSchema.safeParse(body);
  if (!parsed.success) {
    throw new CheckoutContractError(
      "/api/stripe/checkout-session",
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    );
  }

  const res = await moneyPost("/api/stripe/checkout-session", body);
  const data: unknown = await res.json().catch(() => ({}));

  if (!res.ok) {
    const errorBody = data as { error?: string; message?: string };
    return {
      ok: false,
      message:
        typeof errorBody.message === "string"
          ? errorBody.message
          : "Stripe checkout failed.",
      error: typeof errorBody.error === "string" ? errorBody.error : undefined,
    };
  }

  // Boundary parse — Parse Don't Validate. The route (Step 3) runs the same
  // schema before responding, so a mismatch here signals producer/consumer
  // drift rather than a buyer-facing failure; fold it into the `ok: false`
  // banner path (this builder is documented not to throw on response shape)
  // instead of propagating a raw ZodError to the component.
  try {
    const session = CreateStripeCheckoutSessionResponseSchema.parse(data);
    return { ok: true, ...session };
  } catch (err) {
    if (err instanceof z.ZodError) {
      console.error(
        "[createStripeCheckoutSession] response failed contract validation:",
        err.issues,
      );
      return {
        ok: false,
        message: "Stripe checkout session response was malformed.",
        error: err.message,
      };
    }
    throw err;
  }
}
